import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Loader2, Merge, TrendingUp, TrendingDown, Plus, Trash2, Wand2 } from "lucide-react";

const H = ({ children, left, sub }) => (
  <th className={`bg-[#2F5496] text-white text-xs font-semibold py-1.5 px-2 border border-slate-300 whitespace-nowrap ${left ? "text-left" : "text-center"}`}>{children}{sub && <div className="font-normal text-[10px] opacity-80">{sub}</div>}</th>
);
const norm = (s) => (s || "").toUpperCase().replace(/[^A-Z]/g, "");

// Suggest merges: names sharing their first 3 letters (ignoring dots/spaces) go to the most-used one.
const suggest = (known, aliases) => {
  const groups = {};
  known.forEach(k => { const n = norm(k.name); if (n.length >= 2) (groups[n.slice(0, 3)] ||= []).push(k); });
  const out = [];
  Object.values(groups).forEach(g => {
    if (g.length < 2) return;
    const main = [...g].sort((a, b) => b.rows - a.rows)[0].name;
    g.forEach(k => { if (k.name !== main && !aliases[k.name]) out.push([k.name, main]); });
  });
  return out;
};

const MergeRules = ({ open, onOpenChange, onSaved }) => {
  const [rules, setRules] = useState([]);
  const [known, setKnown] = useState([]);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/reports/packer-aliases`, { headers: getAuthHeader() }).then(r => {
      setRules(Object.entries(r.data.aliases)); setKnown(r.data.known_packers);
    });
  }, [open]);
  const aliasMap = Object.fromEntries(rules);
  const sugg = useMemo(() => suggest(known, aliasMap), [known, rules]);
  const set = (i, j, v) => setRules(rs => rs.map((r, x) => (x === i ? (j ? [r[0], v] : [v, r[1]]) : r)));
  const save = async () => {
    try {
      await axios.put(`${API}/reports/packer-aliases`, { aliases: Object.fromEntries(rules.filter(r => r[0] && r[1])) }, { headers: getAuthHeader() });
      toast.success("Packer merge rules saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save rules"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="packer-merge-dialog">
        <DialogHeader><DialogTitle>Packer name merge rules</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Each rule counts the variant under the main name in the Daily and Weekly packer reports. The data in Database A is not changed.</p>
        <datalist id="packer-names">{known.map(k => <option key={k.name} value={k.name}>{k.rows} rows</option>)}</datalist>
        <div className="space-y-1.5">
          {rules.map(([a, c], i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`merge-rule-${i}`}>
              <input list="packer-names" value={a} onChange={e => set(i, 0, e.target.value.toUpperCase())} placeholder="Variant e.g. LAX" className="flex-1 px-2 py-1 border rounded text-sm" data-testid={`merge-alias-${i}`} />
              <span className="text-slate-400 text-sm">→</span>
              <input list="packer-names" value={c} onChange={e => set(i, 1, e.target.value.toUpperCase())} placeholder="Main name e.g. LAXMI" className="flex-1 px-2 py-1 border rounded text-sm" data-testid={`merge-main-${i}`} />
              <button onClick={() => setRules(rs => rs.filter((_, x) => x !== i))} className="text-rose-500 hover:text-rose-700" data-testid={`merge-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setRules(rs => [...rs, ["", ""]])} className="inline-flex items-center gap-1 text-xs text-blue-700" data-testid="merge-add"><Plus className="w-3.5 h-3.5" />Add rule</button>
        </div>
        {sugg.length > 0 && (
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-2" data-testid="merge-suggestions">
            <div className="flex items-center gap-2 text-xs font-semibold text-violet-800 mb-1"><Wand2 className="w-3.5 h-3.5" />Suggested ({sugg.length}) — check before adding
              <button onClick={() => setRules(rs => [...rs, ...sugg])} className="ml-auto underline" data-testid="merge-add-all-suggestions">Add all</button></div>
            <div className="flex flex-wrap gap-1">
              {sugg.map(([a, c]) => (
                <button key={a} onClick={() => setRules(rs => [...rs, [a, c]])} className="rounded bg-white border border-violet-200 px-1.5 py-0.5 text-[11px] hover:bg-violet-100" data-testid={`merge-suggest-${a}`}>{a} → {c}</button>
              ))}
            </div>
          </div>
        )}
        <button onClick={save} className="w-full px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold" data-testid="merge-save">Save rules</button>
      </DialogContent>
    </Dialog>
  );
};

const Change = ({ r }) => {
  if (r.is_new) return <span className="text-sky-700 text-xs font-semibold">NEW</span>;
  if (r.change_pct == null) return <span className="text-slate-300">—</span>;
  const up = r.change_pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}><Icon className="w-3.5 h-3.5" />{up ? "+" : ""}{r.change_pct}%</span>;
};

export const PackerWeekly = ({ isAdmin }) => {
  const [weeks, setWeeks] = useState(8);
  const [end, setEnd] = useState("");
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [mergeOpen, setMergeOpen] = useState(false);
  useEffect(() => {
    setD(null);
    axios.get(`${API}/reports/packers/weekly`, { headers: getAuthHeader(), params: { weeks, ...(end ? { end } : {}) } })
      .then(r => setD(r.data)).catch(() => { setD({ weeks: [], rows: [] }); toast.error("Could not load weekly scorecard"); });
  }, [weeks, end, tick]);
  const download = async () => {
    try {
      const r = await axios.get(`${API}/reports/packers/weekly/excel`, { headers: getAuthHeader(), params: { weeks, ...(end ? { end } : {}) }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([r.data])); a.download = "Packer_Weekly.xlsx"; a.click();
    } catch { toast.error("Excel download failed"); }
  };
  const tot = (wk, k) => (d?.rows || []).reduce((n, r) => n + r.weeks[wk][k], 0);
  return (
    <div className="p-4 pt-2" data-testid="packer-weekly">
      <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
        <select value={weeks} onChange={e => setWeeks(Number(e.target.value))} className="px-2 py-1.5 border border-slate-300 rounded-lg" data-testid="packer-weeks-select">
          {[4, 8, 12, 26].map(n => <option key={n} value={n}>Last {n} weeks</option>)}
        </select>
        <label className="text-xs text-slate-500">up to week of</label>
        <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-lg" data-testid="packer-weeks-end" />
        <button onClick={download} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="packer-weekly-excel"><Download className="w-3.5 h-3.5" />Excel</button>
        {isAdmin && <button onClick={() => setMergeOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-violet-300 text-violet-800 hover:bg-violet-50" data-testid="packer-merge-btn"><Merge className="w-3.5 h-3.5" />Merge names{d?.aliases && Object.keys(d.aliases).length ? ` (${Object.keys(d.aliases).length})` : ""}</button>}
      </div>
      {!d ? <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="packer-weekly-table">
            <thead><tr><H left>Packer</H>{d.weeks.map(w => <H key={w.start} sub="parcels · kg">{w.label}</H>)}<H sub="parcels · kg">Total</H><H>This wk vs last</H></tr></thead>
            <tbody>
              {d.rows.length === 0 && <tr><td colSpan={d.weeks.length + 3} className="p-6 text-center text-slate-500 text-sm" data-testid="packer-weekly-empty">No packer initials in these weeks — import a packing book or scan weight sheets.</td></tr>}
              {d.rows.map(r => (
                <tr key={r.packer} className="hover:bg-slate-50" data-testid={`packer-weekly-row-${r.packer}`}>
                  <td className="py-1.5 px-2 text-sm font-bold text-violet-700 border border-slate-200">{r.packer}</td>
                  {d.weeks.map(w => { const v = r.weeks[w.start]; return (
                    <td key={w.start} className="py-1.5 px-2 text-xs text-center border border-slate-200">{v.parcels ? <><b>{v.parcels}</b> · {v.kg}</> : <span className="text-slate-300">—</span>}</td>); })}
                  <td className="py-1.5 px-2 text-xs text-center border border-slate-200 bg-slate-50"><b>{r.total_parcels}</b> · {r.total_kg}</td>
                  <td className="py-1.5 px-2 text-center border border-slate-200"><Change r={r} /></td>
                </tr>
              ))}
              {d.rows.length > 0 && (
                <tr className="bg-[#D6E4F0] font-bold text-xs" data-testid="packer-weekly-total">
                  <td className="py-1.5 px-2 border border-slate-300">Total</td>
                  {d.weeks.map(w => <td key={w.start} className="py-1.5 px-2 text-center border border-slate-300">{tot(w.start, "parcels")} · {Math.round(tot(w.start, "kg") * 100) / 100}</td>)}
                  <td className="py-1.5 px-2 text-center border border-slate-300">{d.rows.reduce((n, r) => n + r.total_parcels, 0)}</td><td className="border border-slate-300" />
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-slate-500">Weeks run Monday–Sunday by parcel date; weight counted once per parcel; cancelled parcels excluded. The latest week may still be in progress.</p>
        </div>
      )}
      <MergeRules open={mergeOpen} onOpenChange={setMergeOpen} onSaved={() => setTick(t => t + 1)} />
    </div>
  );
};

export const PackersView = ({ daily, isAdmin }) => {
  const [view, setView] = useState("daily");
  return (
    <div>
      <div className="flex gap-1 mx-4 mt-2 bg-slate-100 rounded-lg p-0.5 w-fit">
        {[["daily", "Daily"], ["weekly", "Weekly scorecard"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`packers-view-${k}`}
            className={`px-3 py-1 rounded-md text-xs font-medium ${view === k ? "bg-white text-blue-600 shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {view === "daily" ? daily : <PackerWeekly isAdmin={isAdmin} />}
    </div>
  );
};
